import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useThrottledCallback } from '../../hooks/useThrottledCallback';
import Dropdown from '../_radix-ui/dropdown/Dropdown';
import Button from '../Button';
import type { ColorPickerSize } from './ColorPicker';
import { ColorPicker } from './ColorPicker';
import styles from './ColorPickerDropdown.module.scss';
import { defaultColorPalette, stripHexPrefix } from './color-picker.utils';
import { HexColorInput } from './HexColorInput';

export interface ColorPickerDropdownProps {
  /** Current hex color value (without `#`). */
  color: string;
  /** Size of the trigger button and color swatches. */
  size?: ColorPickerSize;
  /** Disable interaction. */
  disabled?: boolean;
  /** Called when the color changes (value without `#`). */
  onChange: (color: string) => void;
  /** Class name for the trigger button. */
  className?: string;
  /** Optional preview renderer. Receives the current color (without `#`). */
  preview?: (color: string) => React.ReactElement;
}

export const ColorPickerDropdown: React.FC<ColorPickerDropdownProps> = ({
  color: colorProp,
  size,
  disabled,
  onChange,
  className,
  preview,
}) => {
  const [color, setColorInState] = useState(colorProp);
  const onChangeThrottled = useThrottledCallback(onChange, 300);

  const handleColorChange = useCallback(
    (c: string) => {
      const colorOnly = stripHexPrefix(c);
      setColorInState(colorOnly);
      onChangeThrottled(colorOnly);
    },
    [onChangeThrottled]
  );

  useEffect(() => {
    setColorInState(colorProp);
  }, [colorProp]);

  const [isOpen, setIsOpen] = useState(false);

  const handleOpenAutoFocus = useCallback((event: Event) => {
    event.preventDefault();
  }, []);

  const colorOptions = useMemo(() => defaultColorPalette, []);

  return (
    <Dropdown
      className={styles.dropdownContent}
      open={isOpen}
      onOpenAutoFocus={handleOpenAutoFocus}
      onOpenChange={setIsOpen}
      variant="popover"
      trigger={
        <Button className={className || styles.button} priority="medium" size={size}>
          <div className={styles.buttonColorPreview} style={{ backgroundColor: `#${colorProp}` }} />
        </Button>
      }
    >
      <div className={styles.dropdownContainer}>
        {preview && (
          <div className={styles.preview}>
            <div className={styles.previewTitle}>Preview</div>
            <div>{preview(stripHexPrefix(color))}</div>
          </div>
        )}
        {disabled ? (
          <div className={styles.colorGrid}>
            <div className={styles.singleSwatch} style={{ backgroundColor: `#${colorProp}` }} />
          </div>
        ) : (
          <ColorPicker
            className={styles.colorGrid}
            color={color}
            colors={colorOptions}
            onChange={handleColorChange}
            size={size}
          />
        )}
        <HexColorInput color={color} disabled={disabled} onChange={handleColorChange} />
      </div>
    </Dropdown>
  );
};

ColorPickerDropdown.displayName = 'ColorPickerDropdown';
